import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { COLORS } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import {
  buildBodyReport,
  BODY_TYPE_MATRIX,
  fmtReportNum,
} from "../../../utils/bodyReport";
import bodyImg from "../../../assets/img/body.png";

/**
 * BodyCompositionReport — kilo ölçüm drawer'ındaki bölümlü vücut kompozisyon
 * analiz raporu. Bölüm modeli utils/bodyReport motorundan gelir; bu organizma
 * yalnız çizer. Mobil karşılığı: components/measurements/BodyReport.tsx.
 */

const STATUS_COLOR = { low: COLORS.warningDark, normal: COLORS.successDark, high: COLORS.error };
const STATUS_BG = { low: COLORS.warningLight, normal: COLORS.successLight, high: COLORS.errorLight };

function fmt(v, unit) {
  return `${fmtReportNum(v)}${unit ? ` ${unit}` : ""}`;
}

function StatusChip({ status, label }) {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.25,
        borderRadius: 999,
        bgcolor: STATUS_BG[status],
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: STATUS_COLOR[status] }}>
        {label}
      </Typography>
    </Box>
  );
}

function SectionCardBox({ title, action, children }) {
  return (
    <Box
      sx={{
        border: `1px solid ${COLORS.slate200}`,
        borderRadius: 3,
        p: 2,
        bgcolor: COLORS.white,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      {title ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: COLORS.slate800 }}>
            {title}
          </Typography>
          {action || null}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

function RangeBar({ row }) {
  const hasRange = Boolean(row.range && row.status);
  let pos = 0.5;
  if (hasRange) {
    const span = Math.max(row.range.max - row.range.min, 0.0001);
    const lo = row.range.min - span;
    pos = Math.min(Math.max((row.value - lo) / (3 * span), 0.02), 0.98);
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 700, color: COLORS.slate500 }}>
          {row.label}
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: COLORS.slate800 }}>
          {fmt(row.value, row.unit)}
        </Typography>
        {hasRange ? <StatusChip status={row.status} label={row.statusLabel} /> : null}
      </Box>
      {hasRange ? (
        <>
          <Box sx={{ position: "relative", display: "flex", height: 8, borderRadius: 999, overflow: "hidden" }}>
            <Box sx={{ flex: 1, bgcolor: COLORS.warningLight }} />
            <Box sx={{ flex: 1, bgcolor: COLORS.successLight }} />
            <Box sx={{ flex: 1, bgcolor: COLORS.errorLight }} />
            <Box
              sx={{
                position: "absolute",
                left: `${pos * 100}%`,
                top: -2,
                width: 12,
                height: 12,
                borderRadius: "50%",
                ml: "-6px",
                bgcolor: COLORS.slate800,
                border: `2px solid ${COLORS.white}`,
              }}
            />
          </Box>
          <Box sx={{ display: "flex" }}>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: "0.65rem", color: COLORS.slate400 }}>
              {fmtReportNum(row.range.min)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: "0.65rem", color: COLORS.slate400 }}>
              {fmtReportNum(row.range.max)}
            </Typography>
            <Box sx={{ flex: 1 }} />
          </Box>
        </>
      ) : null}
    </Box>
  );
}

function Donut({ rows, weightKg, primary }) {
  const size = 132;
  const r = 52;
  const c = 2 * Math.PI * r;
  const total = weightKg ?? rows.reduce((a, b) => a + b.value, 0);
  const segColors = [primary, alpha(primary, 0.65), alpha(primary, 0.4), alpha(primary, 0.2)];
  let offset = 0;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
      <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r} stroke={COLORS.slate100} strokeWidth={14} fill="none" />
          {rows.map((row, i) => {
            const frac = total > 0 ? Math.max(0, Math.min(row.value / total, 1 - offset)) : 0;
            const seg = (
              <circle
                key={row.key}
                cx={size / 2}
                cy={size / 2}
                r={r}
                stroke={segColors[i % segColors.length]}
                strokeWidth={14}
                fill="none"
                strokeDasharray={`${frac * c} ${c}`}
                strokeDashoffset={-offset * c}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += frac;
            return seg;
          })}
        </svg>
        {weightKg !== undefined ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ fontSize: "1rem", fontWeight: 900, color: COLORS.slate800 }}>
              {fmtReportNum(weightKg)}
            </Typography>
            <Typography sx={{ fontSize: "0.65rem", color: COLORS.slate400 }}>kg</Typography>
          </Box>
        ) : null}
      </Box>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {rows.map((row, i) => (
          <Box key={row.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: segColors[i % segColors.length] }} />
            <Typography sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 700, color: COLORS.slate500 }}>
              {row.label}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: COLORS.slate800 }}>
              {fmt(row.value, row.unit)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Matrix({ fatLevel, muscleLevel, primary }) {
  const fatRows = ["high", "normal", "low"];
  const muscleCols = ["low", "normal", "high"];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      <Typography sx={{ fontSize: "0.65rem", color: COLORS.slate400 }}>
        Dikey: Yağ Oranı · Yatay: Kas Kütlesi
      </Typography>
      {fatRows.map((f) => (
        <Box key={f} sx={{ display: "flex", gap: 0.75 }}>
          {muscleCols.map((m) => {
            const active = f === fatLevel && m === muscleLevel;
            return (
              <Box
                key={m}
                sx={{
                  flex: 1,
                  minHeight: 40,
                  borderRadius: 2,
                  border: `1px solid ${active ? primary : COLORS.slate100}`,
                  bgcolor: active ? alpha(primary, 0.1) : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: active ? 800 : 600,
                    color: active ? COLORS.slate800 : COLORS.slate400,
                    textAlign: "center",
                  }}
                >
                  {BODY_TYPE_MATRIX[f][m]}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}

function Regions({ regions }) {
  const byRegion = new Map(regions.map((r) => [r.region, r]));
  const cell = (key) => {
    const r = byRegion.get(key);
    if (!r) return null;
    return (
      <Box key={key}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: COLORS.slate500 }}>
          {r.label}
        </Typography>
        {r.kg !== undefined ? (
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: COLORS.slate800 }}>
            {fmtReportNum(r.kg)} kg
          </Typography>
        ) : null}
        {r.pct !== undefined ? (
          <Typography sx={{ fontSize: "0.7rem", color: COLORS.slate400 }}>
            %{fmtReportNum(r.pct)}
          </Typography>
        ) : null}
      </Box>
    );
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {["leftArm", "trunk", "leftLeg"].map(cell)}
      </Box>
      <Box component="img" src={bodyImg} alt="Vücut şeması" sx={{ width: 100, height: 200, objectFit: "contain" }} />
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {["rightArm", "rightLeg"].map(cell)}
      </Box>
    </Box>
  );
}

function Section({ section, primary }) {
  switch (section.kind) {
    case "header":
      return (
        <SectionCardBox>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {section.score !== undefined ? (
              <Box
                sx={{
                  width: 92,
                  height: 92,
                  borderRadius: "50%",
                  border: `4px solid ${primary}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 900, color: primary }}>
                  {fmtReportNum(section.score)}
                </Typography>
                <Typography sx={{ fontSize: "0.6rem", color: COLORS.slate400 }}>Vücut Puanı</Typography>
              </Box>
            ) : null}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
              {section.rows.map((row) => (
                <Box key={row.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 700, color: COLORS.slate500 }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: COLORS.slate800 }}>
                    {fmt(row.value, row.unit)}
                  </Typography>
                  {row.status ? <StatusChip status={row.status} label={row.statusLabel} /> : null}
                </Box>
              ))}
            </Box>
          </Box>
        </SectionCardBox>
      );
    case "donut":
      return (
        <SectionCardBox title={section.title}>
          <Donut rows={section.rows} weightKg={section.weightKg} primary={primary} />
        </SectionCardBox>
      );
    case "bars":
      return (
        <SectionCardBox title={section.title}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {section.rows.map((row) => (
              <RangeBar key={row.key} row={row} />
            ))}
          </Box>
        </SectionCardBox>
      );
    case "pairs":
      return (
        <SectionCardBox title={section.title}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
            {section.rows.map((row) => (
              <Box
                key={row.key}
                sx={{ p: 1.25, borderRadius: 2, bgcolor: COLORS.slate50 }}
              >
                <Typography sx={{ fontSize: "0.65rem", color: COLORS.slate400, fontWeight: 700 }}>
                  {row.label}
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: COLORS.slate800 }}>
                  {fmt(row.value, row.unit)}
                </Typography>
              </Box>
            ))}
          </Box>
        </SectionCardBox>
      );
    case "matrix":
      return (
        <SectionCardBox title={section.title} action={<StatusChip status="normal" label={section.label} />}>
          <Matrix fatLevel={section.fatLevel} muscleLevel={section.muscleLevel} primary={primary} />
        </SectionCardBox>
      );
    case "regions":
      return (
        <SectionCardBox title={section.title}>
          <Regions regions={section.regions} />
        </SectionCardBox>
      );
    case "text":
      return (
        <SectionCardBox title={section.title}>
          <Typography sx={{ fontSize: "0.7rem", color: COLORS.slate400, lineHeight: 1.6 }}>
            {section.text}
          </Typography>
        </SectionCardBox>
      );
    default:
      return null;
  }
}

export default function BodyCompositionReport({ values, profile }) {
  const { palette } = useClinicTheme();
  const report = buildBodyReport(values || [], profile || {});
  if (report.sections.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 2 }}>
      {report.sections.map((s) => (
        <Section key={s.id} section={s} primary={palette.primary} />
      ))}
    </Box>
  );
}
